import { bookedAlready, bookingRefused } from "../../src/booking.ts";
import { dayRefused, hasDay } from "../../src/plan-day.ts";
import type { Item } from "../../src/web/types.ts";
import type { IconName } from "../souvenir/index.js";

// Change with your AI (DESIGN.md, "Change it with your AI, right where I'd look"; features/getting-started.md,
// "Change with your AI"): every word of it, in one place. Where someone would expect to change or add
// something, a card's "Change with your AI" and a view's "Add" open one sheet: what the web does right here,
// each a button, then a prompt to copy that names the trip and the thing, with likely asks as chips. When the
// web gains an edit, it moves from the chips (and the prompt) to the buttons here.

export const ASK_AI = {
  // A card's button, and a view's.
  card: "Change with your AI",
  add: "Add",
  // The sheet.
  title: "Your AI makes changes in Tripmo",
  here: "Right here",
  ask: "Or ask your AI",
  // With nothing to do right here.
  askAlone: "Ask your AI",
  askHint: "Tap an ask, or type your own after the colon, then paste it into your AI.",
  promptLabel: "What to ask your AI",
  copy: "Copy",
  copied: "Copied: paste it into your AI",
  close: "Close",
} as const;

// What a card takes on the web, each its own form's: a day (PlanDay.tsx: a place or goal, still wanted or in the
// plan, src/plan-day.ts), a payment (AddPayment.tsx: anything but a choice, whose option is what's paid for, or
// what's dropped), a city (StayCity.tsx: a stay with a place). Here, with the sheet's words, so the words and what
// they offer stay together, and the check of them (test/web-words.test.ts) reads them without the views.
export const plannable = (item: Item) => dayRefused(item) === null;
export const takesPayment = (item: Item) => item.type !== "choice" && item.status !== "dropped";
export const takesCity = (item: Item) =>
  item.type === "stay" && item.where !== undefined && item.where.precision !== "none";

// What the web changes itself, as the sheet's buttons: on a card (its own), and on a view.
export type CardEdit =
  | "book"
  | "unbook"
  | "plan"
  | "pay"
  | "rate"
  | "city"
  | "todo-done"
  | "todo-owner"
  | "todo-due"
  | "todo-important"
  | "todo-remove"
  | "todo-add"
  | "answer"
  | "close-ask"
  | "file"
  | "photo"
  | "note"
  | "link-add"
  | "links"
  | "remove";
export type ViewEdit =
  | "rate-ideas"
  | "save-link"
  | "plan-day"
  | "trip-dates"
  | "add-todo"
  | "add-document"
  | "currency";
export interface EditButton<E extends string> {
  edit: E;
  label: string;
  icon: IconName;
}

// What a card can do on the web, in the order its own controls come (DESIGN.md section 5, the web's
// read-only rule): its booking (and, booked, not booked after all), its day, a payment, my rating, a stay's city, a
// to-do's own fields or a to-do about it, a question waiting on me or one I asked, files, a note, a link to add and
// its links to edit or remove (DESIGN.md, "What the web adds, it can take away"; features/links.md, "Add, edit and
// remove every link on the web"), and last Remove from trip (DESIGN.md, "Remove from trip"). Nothing on a read-only
// trip.
export function cardEdits(
  item: Item,
  on: {
    // The choice it's an option of, or null.
    choice: Item | null;
    // Whether its booking form can open where it is (the card in full has it; the deck's card too).
    books: boolean;
    asks: number;
    // My own questions about it that are still open, to close.
    mine?: number;
    // How many links it has to change: its own (its website, booking page, email, social pages and other links) and
    // the saved posts and videos about it.
    links: number;
    readOnly: boolean;
  },
): EditButton<CardEdit>[] {
  if (on.readOnly) return [];
  const out: EditButton<CardEdit>[] = [];
  const task = item.type === "task";
  if (on.books && bookingRefused(item, on.choice && item.parent === on.choice.id ? "choice" : undefined) === null) {
    out.push({ edit: "book", label: bookedAlready(item) ? "Edit booking" : "Mark as booked", icon: "lock" });
    if (item.status === "booked") out.push({ edit: "unbook", label: "Not booked after all", icon: "replay" });
  }
  if (plannable(item))
    out.push({ edit: "plan", label: hasDay(item.when) ? "Move to another day" : "Plan for a day", icon: "calendar" });
  if (takesPayment(item)) out.push({ edit: "pay", label: "Add a payment", icon: "plus" });
  if (item.type === "place" || item.type === "goal" || item.status === "option")
    out.push({ edit: "rate", label: "Rate it", icon: "star" });
  if (takesCity(item))
    out.push({ edit: "city", label: item.where?.city ? "Change its city" : "Add its city", icon: "pin" });
  if (task) {
    out.push({ edit: "todo-done", label: item.status === "done" ? "Mark not done" : "Mark done", icon: "check" });
    out.push({ edit: "todo-owner", label: "Change who it's for", icon: "people" });
    out.push({ edit: "todo-due", label: "Change when it's due", icon: "calendar" });
    out.push({
      edit: "todo-important",
      label: item.important ? "Mark not important" : "Mark important",
      icon: "bell",
    });
  } else out.push({ edit: "todo-add", label: "Add a to-do", icon: "plus" });
  if (on.asks > 0) out.push({ edit: "answer", label: "Answer its question", icon: "send" });
  if ((on.mine ?? 0) > 0) out.push({ edit: "close-ask", label: "Close your question", icon: "lock" });
  out.push({ edit: "file", label: "Attach a file", icon: "plus" });
  if (item.type === "place" || item.type === "stay" || item.status === "option" || item.status === "not_chosen")
    out.push({ edit: "photo", label: "Add a photo", icon: "camera" });
  out.push({ edit: "note", label: "Add a note", icon: "note" });
  out.push({ edit: "link-add", label: "Add a link", icon: "plus" });
  if (on.links > 0) out.push({ edit: "links", label: on.links > 1 ? "Edit its links" : "Edit its link", icon: "note" });
  if (task) out.push({ edit: "todo-remove", label: "Remove to-do", icon: "close" });
  // Last, as on the card (DESIGN.md, "Remove from trip"): anything but a to-do, which has its own.
  else out.push({ edit: "remove", label: "Remove from trip", icon: "close" });
  return out;
}

export type AddView = "ideas" | "days" | "bookings";

// What a view's Add can do on the web: rate what waits and save a link on Ideas, plan something for a day and
// change the trip's dates from Days, a to-do, a travel document (kept in the app only: DESIGN.md, "Private to the app,
// never to an AI") and my currency for Bookings' money (`currency`: whether I've chosen one).
export function viewEdits(
  view: AddView,
  on: { toRate: number; unplaced: number; currency: boolean },
): EditButton<ViewEdit>[] {
  if (view === "ideas")
    return [
      ...(on.toRate > 0 ? [{ edit: "rate-ideas" as const, label: "Rate ideas", icon: "star" as const }] : []),
      { edit: "save-link", label: "Save a link", icon: "plus" },
    ];
  if (view === "days")
    return [
      ...(on.unplaced > 0 ? [{ edit: "plan-day" as const, label: "Plan for a day", icon: "calendar" as const }] : []),
      { edit: "trip-dates", label: "Change the trip's name or dates", icon: "note" },
    ];
  return [
    { edit: "add-todo", label: "Add a to-do", icon: "plus" },
    { edit: "add-document", label: "Add a travel document", icon: "book" },
    { edit: "currency", label: on.currency ? "Change your currency" : "Choose your currency", icon: "bag" },
  ];
}

// The prompt's start, naming the trip and the thing: In my Tripmo trip “Korea 2026”, for “Hotel Metropolitan
// Nagano”: (a view's names the trip alone). Curly quotes, as the prompt's italic draws them well.
export function promptLead(trip: string, thing?: string): string {
  return thing ? `In my Tripmo trip “${trip}”, for “${thing}”: ` : `In my Tripmo trip “${trip}”: `;
}

// An ask after the lead, as a sentence goes on after a colon: "find other hotels for these nights."
export function promptWith(lead: string, ask: string): string {
  const said = ask.charAt(0).toLowerCase() + ask.slice(1);
  return `${lead}${/[.?!]$/.test(said) ? said : `${said}.`}`;
}

// Two or three likely asks for a card, by its kind and whether it's booked (features/getting-started.md).
export function cardAsks(item: Item, choice: Item | null): string[] {
  if (item.type === "choice") return ["Find more options", "Compare the options"];
  const booked = bookedAlready(item);
  const booking = ["Add the cancellation policy", "It was cancelled"];
  if (item.status === "option" || item.status === "not_chosen")
    return [
      ...(item.type === "stay" ? ["Add what it's like to stay at"] : []),
      "Compare it with the other options",
      ...(choice ? ["Find more options"] : []),
    ];
  if (item.type === "stay")
    return booked
      ? [...booking, "Add what it's like to stay at"]
      : ["Find other hotels for these nights", "Add what it's like to stay at"];
  if (item.type === "move")
    return booked ? booking : ["Find other ways to make this trip", "Find out how long it takes"];
  // Not a reminder: a to-do's due date reminds by itself (the todos guide).
  if (item.type === "task") return ["Help me get this done", "Find out what it takes"];
  if (item.type === "goal") return ["Find where we could do this", "Find out what it takes"];
  if (booked) return [...booking, "Find out when it's open"];
  return ["Find what's near it", "Find out when it's open"];
}

// A view's likely asks: Ideas' near our hotel, in its city, when a stay has one.
export function viewAsks(view: AddView, city: string | null): string[] {
  if (view === "ideas")
    return [
      city ? `Suggest things to do near our hotel in ${city}` : "Suggest things we might love doing",
      "Bring in what a friend recommends",
    ];
  if (view === "days") return ["Fit what we want into our days", "Plan a day trip from where we stay"];
  return ["Add a booking from my confirmation email", "What's still to book?"];
}

// The example prompts the views offer, where they're empty and on their first visit (Tips.tsx): each after the
// trip's own lead, so it names the trip it's shown in, never a sample city (DESIGN.md, "The web's words say what
// the web does"). Place-neutral, so a trip with nothing in it yet reads as well as one that's half planned; [in
// brackets], what the person fills in. None asks the AI for what the web does itself (EDIT_WORDS, below).
export const EXAMPLE_ASKS = {
  map: "Add [the places we want to see].",
  mapPlaces: "Look up where everything is, and put it on the map.",
  days: "Fit what we all want into our days.",
  ideas: "Suggest three things we might love doing.",
  friends: "Make a page where [a friend] can write their tips, and give me its link.",
  research: "Is a rail pass worth it for our route? Save what you find.",
  people: "Who hasn't said what they want yet?",
  peopleMe: "Ask me what I'd love to do, and add it.",
  bookings: "Here's a booking confirmation [paste it or attach it]: save it.",
  bookingsKinds: "Go through it and mark what needs booking, and by when.",
  reminders: "Remind me to check in for our flight the day before.",
  money: "Set our budget: [an amount] each.",
  updates: "What changed since yesterday?",
  catchUp: "Catch me up on what's changed since I last looked.",
} as const;
export type ExampleAsk = keyof typeof EXAMPLE_ASKS;

// An example prompt as shown: In my Tripmo trip “Korea 2026”: fit what we all want into our days.
export function examplePrompt(trip: string, ask: ExampleAsk): string {
  return promptWith(promptLead(trip), EXAMPLE_ASKS[ask]);
}

// The words that name each of the web's edits, for the check that no line sends someone to their AI for one
// (DESIGN.md, "The web's words say what the web does"; test/web-words.test.ts): any line's words after "ask
// your AI" (or tell, or give) are matched against them. Every edit has its words, so one the web gains can't
// be left out.
export const EDIT_WORDS: Record<CardEdit | ViewEdit, RegExp> = {
  book: /\bmark(?: it| this| them)? (?:as )?booked\b|\bedit (?:its |the )?booking\b/i,
  unbook: /\bnot booked after all\b/i,
  plan: /\bplan (?:it |this |them )?for a day\b|\bmove (?:it |this )?to another day\b|\btake (?:it )?off its day\b/i,
  pay: /\b(?:add|record|save) (?:a |the )?payments?\b/i,
  rate: /\brat(?:e|ing)\b/i,
  city: /\b(?:add|change|set) (?:its |the )?(?:stay's )?city\b/i,
  "todo-done": /\b(?:mark|tick)(?: it| this| the to-do)?(?: as)? (?:not )?done\b|\btick (?:it |things )?off\b/i,
  "todo-owner": /\b(?:assign|give|hand) (?:it|this|the to-do) to\b|\bwho it's for\b/i,
  "todo-due": /\b(?:change|move|set) (?:when it's due|(?:the |its )?due date)\b/i,
  "todo-important": /\bimportant\b/i,
  "todo-remove": /\bremove (?:it|this|the) to-do\b|\bremove (?:a |the )?to-do\b/i,
  "todo-add": /\b(?:add|make|create) (?:a |the |new )?to-dos?\b/i,
  "close-ask": /\bclose (?:my |your |the |a )?question\b/i,
  answer: /\banswer\b/i,
  file: /\battach (?:a |the )?file\b/i,
  photo: /\badd (?:a )?photo\b/i,
  note: /\badd (?:a )?note\b/i,
  "link-add": /\badd (?:a |the |this )?link\b/i,
  links: /\b(?:edit|change|remove|fix) (?:a |its |the |this )?(?:saved )?links?\b/i,
  remove:
    /\b(?:remove|delete|take) (?:it|this|them|(?:a |the )?(?:place|stay|hotel|option|choice|goal|flight|journey|item|thing))(?: out)? (?:from|of) (?:the |my |our )?trip\b|\bremove from (?:the )?trip\b/i,
  "rate-ideas": /\brat(?:e|ing) (?:the )?ideas\b/i,
  "save-link": /\bsave (?:a |the |this )?link\b/i,
  "plan-day": /\bplan (?:it |this |them |something )?for a day\b/i,
  "add-todo": /\b(?:add|make|create) (?:a |the |new )?to-dos?\b/i,
  // What the app alone keeps (DESIGN.md, "Private to the app, never to an AI"): travel documents, and with them diet
  // and mobility (Trip settings), which no line may send someone to their AI for.
  "add-document": /\btravel documents?\b|\bpassports?\b|\bvisas?\b|\b(?:diet|mobility)\b/i,
  "trip-dates": /\b(?:change|set|move) (?:the )?trip's (?:name|dates)\b|\brename (?:the )?trip\b/i,
  currency: /\b(?:choose|change|set) (?:your|my) currency\b/i,
};

// The web's edits a line names, of those above: none, for a line that sends someone to their AI.
export function editsNamed(words: string): (CardEdit | ViewEdit)[] {
  return (Object.keys(EDIT_WORDS) as (CardEdit | ViewEdit)[]).filter((e) => EDIT_WORDS[e].test(words));
}
